/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2021, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Logger } from '@core/lib/services';
import { NPRAuthentication } from '@app/di/security/npr-authentication.service';
import { SRClientService, MessageType } from '@app/di/integrations/reporting/smartreports/sr-client.service';
import { PostMessengerService } from '@app/di/channel/window/post-messenger.service';

const log = new Logger('SREditorService');

export class SREditorService extends SRClientService {
  private reportId: string = undefined;

  constructor(authenticator: NPRAuthentication, postMessengerService: PostMessengerService) {
    super('Editor', authenticator, postMessengerService);
  }

  public isLoaded() {
    return this.reportId !== undefined;
  }

  public isEmpty() {
    return this.reportId === undefined;
  }

  public getReportId() {
    return this.reportId;
  }

  private setReportLoaded = (response: any) => {
    this.reportId = response.reportId;
    this.showContents();
    return response;
  };

  private setReportUnloaded = (response?: any) => {
    this.reportId = undefined;
    this.hideContents();
    return response;
  };

  private clientFailedToOpenWindow() {
    log.error('Failed to open client window');
    return Promise.reject('Failed to open client window');
  }

  public async createReport(params: any) {
    log.debug('Creating report', params);
    if (await this.show()) {
      const type = !!params.patientInformation ? MessageType.CreateUnspecified : MessageType.Create;
      if (type === MessageType.CreateUnspecified) {
        delete params.version;
      }
      const promise = this.sendRequest(type, params).then(this.setReportLoaded);
      this.showContents();
      return promise;
    } else {
      return this.clientFailedToOpenWindow();
    }
  }

  public async openReport(reportId: string): Promise<any> {
    log.debug('Loading report', reportId);
    if (await this.show()) {
      return this.sendRequest(MessageType.Open, { reportId: reportId }).then(this.setReportLoaded);
    } else {
      return this.clientFailedToOpenWindow();
    }
  }

  public saveReport() {
    log.debug('Saving current report');
    return this.sendRequest(MessageType.Save);
  }

  public discardReport() {
    log.debug('Discarding changes in current report');
    return this.sendRequest(MessageType.Discard);
  }

  public async addendReport(reportId: string) {
    if (await this.show()) {
      return this.sendRequest(MessageType.Addend, { reportId: reportId }).then(this.setReportLoaded);
    } else {
      return this.clientFailedToOpenWindow();
    }
  }

  public async addendLegacyReport(params: any) {
    if (await this.show()) {
      const promise = this.sendRequest(MessageType.AddendLegacyReport, params).then(this.setReportLoaded);
      this.showContents();
      return promise;
    } else {
      return this.clientFailedToOpenWindow();
    }
  }

  public closeReport(): Promise<any> {
    if (this.isEmpty()) {
      return Promise.resolve('empty');
    }
    log.debug('Closing current report');
    return this.sendRequest(MessageType.Close).then(this.setReportUnloaded);
  }

  public deleteReport() {
    log.debug('Deleting current report');
    return this.sendRequest(MessageType.Delete).then(this.setReportUnloaded);
  }

  public clear(reportId: string) {
    if (this.reportId === reportId) {
      log.debug('Clear loaded state of report:', reportId);
      this.setReportUnloaded();
    }
  }

  public reset() {
    this.setReportUnloaded();
  }

  /**
   * This temporary API is used as a workaround solution for the missing associated exams in openReport's response
   * This will be removed in future when proper fix is done on SmartReporting
   */
  public getAssociatedExams(resourceService: any) {
    if (this.isEmpty()) {
      return Promise.resolve(null);
    }
    return this.getReportAssociatedExams(resourceService, this.reportId);
  }

  public async addToAssociation(accession: any, instituteId: any, issueridRequired: boolean) {
    log.debug('Add exam to association', accession);
    if (this.isLoaded()) {
      const params: any = {
        version: 'v1',
        reportId: this.reportId,
        associatedExamToAdd: {
          accessionNumber: accession
        }
      };

      // Add issuerId only if instituteId is defined
      if (issueridRequired) {
        params.associatedExamToAdd.issuerId = instituteId;
      }

      return this.sendRequest(MessageType.AddToAssociation, params);
    } else {
      return this.clientFailedToOpenWindow();
    }
  }

  public async removeFromAssociation(accession: any, instituteId: any, issueridRequired: boolean) {
    log.debug('Remove exam from association', accession);
    if (this.isLoaded()) {
      const params: any = {
        version: 'v1',
        reportId: this.reportId,
        associatedExamToRemove: {
          accessionNumber: accession
        }
      };

      // Add issuerId only if instituteId is defined
      if (issueridRequired) {
        params.associatedExamToRemove.issuerId = instituteId;
      }

      return this.sendRequest(MessageType.RemoveFromAssociation, params);
    } else {
      return this.clientFailedToOpenWindow();
    }
  }
}
